import React from "react";
import { useLocation } from "react-router-dom";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { get } from "lodash";
import { push } from "store/actions/router";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import EmployerGroups from "./EmployerGroups";
import BankAccounts from "./BankAccounts";
import ProfileInfo from "./ProfileInfo";
import UkgCredentials from "./UkgCredentials";
import IconSpinner from "components/IconSpinner";

import { createErrorSelector } from "store/selectors";
import { isEmployerAdminSelector } from "store/selectors/user";
import { employerConstants } from "actions/types";
import {
  getAdministrators,
  getAllEmployerAccounts,
  getAllEmployerGroups,
  getEmployerWalletDetails,
} from "actions/employerActions";
import Administrators from "../profile/Administrators";
import {
  employerHasFullyKybd,
  hasEnabledUkgPayrollIntegration,
} from "store/selectors/employer";
import type { RootState } from "types/state";

const actions = [
  employerConstants.GET_EMPLOYER_ACCOUNTS,
  employerConstants.GET_EMPLOYER_GROUPS,
  employerConstants.GET_EMPLOYER_WALLET_DETAILS,
];
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isEmployerAdmin: isEmployerAdminSelector(state),
    hasVirtualBank: get(state.employer, "hasVirtualBank") as
      | boolean
      | undefined,
    employerHasFullyKybd: employerHasFullyKybd(state),
    hasUkgPayrollIntegration: hasEnabledUkgPayrollIntegration(state),
  };
};

const mapDispatchToProps = {
  push,
  getAllEmployerGroups,
  getAllEmployerAccounts,
  getAdministrators,
  getEmployerWalletDetails,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// `routeName` is injected by the CompanyProfile_WithLocation wrapper below.
interface OwnProps {
  routeName: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  tab: string;
  initialFetching: boolean;
}

class CompanyProfile extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      tab: this._calcActiveTab(props.routeName),
      initialFetching: true,
    };
  }

  componentDidMount() {
    window.analytics.page("Employer Company Profile");
    // hydration calls for data needed by sub components
    // heterogeneous thunk results — typed as unknown so the wallet/admin
    // promises pushed below fit the same array (Promise.all only awaits them)
    const requests: Promise<unknown>[] = [
      this.props.getAllEmployerGroups(
        this.props.client as ApolloClient<object>
      ),
      this.props.getAllEmployerAccounts(
        this.props.client as ApolloClient<object>
      ),
    ];

    // wallet only going to exist if they have kyb'd fully
    if (this.props.employerHasFullyKybd) {
      requests.push(
        this.props.getEmployerWalletDetails(
          this.props.client as ApolloClient<object>
        )
      );
    }
    if (this.props.isEmployerAdmin) {
      requests.push(
        this.props.getAdministrators(this.props.client as ApolloClient<object>)
      );
    }
    void Promise.all(requests).then(() =>
      this.setState({ initialFetching: false })
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.routeName !== this.props.routeName) {
      this.setState({ tab: this._calcActiveTab(this.props.routeName) });
    }
  }

  _calcActiveTab = (routeName: string) => {
    if (
      this.props.isEmployerAdmin &&
      routeName === "/dashboard/company/administrators"
    ) {
      return "administrators";
    }

    if (
      this.props.hasUkgPayrollIntegration &&
      routeName === "/dashboard/company/integrations"
    ) {
      return "integrations";
    }

    switch (routeName) {
      case "/dashboard/company/bank":
        return "bank";
      case "/dashboard/company/info":
        return "info";
      case "/dashboard/company/group":
        return "group";
      default:
        return "info";
    }
  };

  tabUpdate(value: { k: string }) {
    this.setState({ tab: value.k });
    this.props.push("/dashboard/company/" + value.k);
  }

  _getCardContent = () => {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }
    switch (this.state.tab) {
      case "bank":
        return <BankAccounts />;
      case "info":
        return <ProfileInfo />;
      case "group":
        return <EmployerGroups />;
      case "administrators":
        return <Administrators />;
      case "integrations":
        return <UkgCredentials />;
    }
  };

  render() {
    const content = this._getCardContent();

    return (
      <div className="mega-container">
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">Company Profile</h1>
            <p className="page-subtext">
              Manage all aspects of your company and plan setup.
            </p>
            <div className="main-content">{content}</div>
          </article>
        </section>
      </div>
    );
  }
}

const CompanyProfile_WithLocation = (props: Omit<Props, keyof OwnProps>) => {
  const { pathname: routeName } = useLocation();
  return <CompanyProfile {...props} routeName={routeName} />;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withApollo<Props>(CompanyProfile_WithLocation));
